// scheduled-store.svelte.ts — the "Scheduled" sidebar unread badge.
//
// A timer-fired scheduled run creates its chat server-side, with no browser involved,
// so nothing tells the open tab. The layout polls listScheduledChats() and this store
// flips an unread dot when a run completed AFTER the last time the user opened the
// Scheduled overview. Visiting /scheduled clears it. Module-level $state is shared
// across every importer (Svelte 5), so the nav dot re-renders reactively.

import { untrack } from 'svelte';
import { listScheduledChats, type ScheduledChat } from './scheduled-api';
import {
	capOpened,
	foldRunSessions,
	isUnread,
	recordOpened,
	type OpenedMap,
	type RunSessionMap,
} from './scheduled-unread';

const SEEN_KEY = 'sayano:scheduled-seen';
// Per-session "opened" timestamps — for the sidebar's green unread dot on a
// chat that a timer-fired scheduled run created. Keyed by sessionId → epoch ms
// when the user last opened it. Persisted so the dot stays cleared across reloads.
const SESSION_SEEN_KEY = 'sayano:scheduled-session-seen';
const SESSION_SEEN_CAP = 1000; // bound localStorage growth (keep the newest opens)

// Newest run-completion epoch (ms) observed from the server across all the user's
// schedules. Monotonic — only ever moves forward.
let latestRunAt = $state(0);
let hasUnread = $state(false);

// sessionId → run epoch (ms) for the LATEST finished run of each schedule, and
// sessionId → epoch the user last opened it (seeded from localStorage). The pure
// decision logic lives in scheduled-unread.ts; this module is just the reactive
// + storage glue.
let runSessions = $state<RunSessionMap>({});
let openedAt = $state<OpenedMap>(loadOpened());

function loadOpened(): OpenedMap {
	if (typeof localStorage === 'undefined') return {};
	try {
		const raw = localStorage.getItem(SESSION_SEEN_KEY);
		const parsed = raw ? (JSON.parse(raw) as unknown) : null;
		return parsed && typeof parsed === 'object' ? (parsed as OpenedMap) : {};
	} catch {
		return {};
	}
}

function persistOpened(map: OpenedMap): void {
	if (typeof localStorage === 'undefined') return;
	try {
		localStorage.setItem(SESSION_SEEN_KEY, JSON.stringify(capOpened(map, SESSION_SEEN_CAP)));
	} catch {
		/* quota / private mode — the in-memory map still drives this tab */
	}
}

function seenAt(): number {
	if (typeof localStorage === 'undefined') return 0;
	return Number(localStorage.getItem(SEEN_KEY) || 0);
}

function recompute(): void {
	hasUnread = latestRunAt > seenAt();
}

/** Reactive accessor for the nav dot. */
export function scheduledHasUnread(): boolean {
	return hasUnread;
}

/** Reactive: is this sidebar session a scheduled run the user hasn't opened
 *  since it fired? Drives the green "new & unread" dot in SessionList. */
export function isScheduledUnread(sessionId: string): boolean {
	return isUnread(runSessions, openedAt, sessionId);
}

/** Record that the user opened a session — clears its unread dot. Recorded for
 *  ANY session (not just known scheduled ones) so opening a fresh run BEFORE the
 *  next poll learns about it still wins: when the poll later maps the run, the
 *  open timestamp already beats the run time. */
export function markScheduledSessionSeen(sessionId: string, at: number = Date.now()): void {
	// Read UNTRACKED. SessionList calls this from `$effect(() => markScheduledSessionSeen(activeId))`;
	// a tracked read of `openedAt` made that effect depend on the very map it writes, so each
	// write re-ran it, a later Date.now() produced a new map, and it re-ran again — until
	// Svelte threw `effect_update_depth_exceeded` and aborted the chat page's mount batch
	// (prod 2026-09-18: every SPA nav into /s/<id> for heavy users; the first message never
	// sent). It only settled when two runs shared a millisecond, which is why light users
	// rarely saw it. Pinned by scheduled-store.effect-loop.test.ts.
	const current = untrack(() => openedAt);
	const next = recordOpened(current, sessionId, at);
	if (next === current) return; // no change — skip the reactive write
	openedAt = next;
	persistOpened(next);
}

/** Fold a freshly-loaded set of schedules into the badge + unread map (no fetch). */
export function noteScheduledChats(
	chats: Pick<ScheduledChat, 'lastRunAt' | 'lastRunSessionId' | 'lastRunStatus'>[],
): void {
	// A 'skipped' run is a conditional run the agent kept silent — it must not light the
	// nav dot, so exclude it from the latest-run high-water mark (parity with foldRunSessions).
	const max = chats.reduce(
		(m, c) => Math.max(m, c.lastRunAt && c.lastRunStatus !== 'skipped' ? Date.parse(c.lastRunAt) : 0),
		0,
	);
	if (max > latestRunAt) latestRunAt = max;
	runSessions = foldRunSessions(runSessions, chats);
	recompute();
}

/** Poll the schedules to refresh the badge (called on the layout's periodic tick). */
export async function pollScheduledBadge(): Promise<void> {
	try {
		noteScheduledChats(await listScheduledChats());
	} catch {
		/* transient — the next tick retries */
	}
}

/** The user opened the Scheduled overview — everything up to the latest known run is seen. */
export function markScheduledSeen(): void {
	if (typeof localStorage !== 'undefined') localStorage.setItem(SEEN_KEY, String(latestRunAt));
	hasUnread = false;
}
